import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoCourseYear } from "@/shared/model/edo-course-year.model";
import { IEdoSubject } from "@/shared/model/edo-subject.model";
import { IEdoWikiPost, defaultValue } from "@/shared/model/edo-wiki-post.model";
import { EdoEducationalStage } from "@/shared/model/enumerations/edo-educational-stage.model";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { getEntities as getEdoClasseList } from "@/shared/reducers/entities/edo-class.reducer";
import { AxiosResponse } from "axios";
import axios from "axios";
import "moment/locale/it";
import React, { createRef, useEffect, useState } from "react";
import { Translate, translate } from "react-jhipster";
import { Loader2 } from "lucide-react";

interface IMultiUploadProps {
  open: boolean;
  onClose: () => void;
}

const MultiUpload = (props: IMultiUploadProps) => {
  const dispatch = useAppDispatch();
  const { loading, updating } = useAppSelector<EntityState<IEdoWikiPost>>(
    (state) => state.edoWikiPost
  );
  const { entities: edoSubjectList, loading: loadingSubject } = useAppSelector<
    EntityState<IEdoSubject>
  >((state) => state.edoSubject);
  const { entities: edoCourseYearList } = useAppSelector<EntityState<IEdoCourseYear>>(
    (state) => state.edoCourseYear
  );
  const [form, setForm] = useState<IEdoWikiPost>({ ...defaultValue });
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadResults, setUploadResults] = useState<
    { title: string; success: boolean }[]
  >([]);
  const fileInput = createRef<HTMLInputElement>();
  const { open, onClose: handleClose } = props;

  useEffect(() => {
    dispatch(getEdoClasseList({ page: 0, size: 1000, sort: "id:ASC" }));
  }, []);

  useEffect(() => {
    if (!open) {
      setForm({ ...defaultValue });
      setUploadResults([]);
      return;
    }
  }, [open]);

  const selectFiles = () => {
    fileInput.current?.click();
  };

  const handleFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploading(true);
    const files = [...Array.from(event.target.files || [])];
    if (files.length) {
      const fileResponses: AxiosResponse<{ data: IEdoWikiPost }>[] = [];
      const failedUploads: File[] = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file, file.name);
        const { title, ...documentToSend } = form;
        formData.append("data", JSON.stringify(documentToSend));
        try {
          const response = await axios.post<{ data: IEdoWikiPost }>(
            "/wiki-posts/multiple",
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 0,
              maxBodyLength: Infinity,
              maxContentLength: Infinity,
            }
          );
          fileResponses.push(response);
          setUploadResults((prev) => [...prev, { title: file.name, success: true }]);
        } catch (e) {
          failedUploads.push(file);
          setUploadResults((prev) => [...prev, { title: file.name, success: false }]);
        }
      }
      setUploading(false);
      if (
        fileResponses.filter((r) => ![200, 201, 204].includes(r.status)).length === 0
      ) {
        handleClose();
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Caricamento multiplo</DialogTitle>
        </DialogHeader>
        <p className="mb-4 text-sm text-[var(--color-mockup-text-light)]">
          Inserisci le informazioni dei contenuti wiki. Puoi selezionare uno o più
          anni e uno o più istituti.
        </p>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[var(--color-mockup-text)]">
                Anni <span className="text-[var(--color-mockup-coral)]">*</span>
              </label>
              <select
                multiple
                value={form.years?.map((el) => String(el.id)) || []}
                onChange={(e) => {
                  const value = Array.from(
                    e.target.selectedOptions,
                    (o) => o.value
                  ).map((v) => parseInt(v, 10));
                  setForm({
                    ...form,
                    years: value.map((id) => ({ id })),
                  } as any);
                }}
                disabled={uploading}
                className="flex min-h-[80px] w-full rounded-2xl border-2 border-[var(--color-mockup-border)] bg-surface px-3 py-2 text-sm focus:outline-none focus:border-primary focus:shadow-[0_4px_12px_rgba(18,181,170,0.15)] transition-all duration-200"
              >
                {edoCourseYearList?.map((annoCorso) => (
                  <option key={annoCorso.id} value={annoCorso.id}>
                    {annoCorso.year}
                  </option>
                )) ?? null}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[var(--color-mockup-text)]">
                {translate("edocendoApp.edoVideostorePost.subject")}{" "}
                <span className="text-[var(--color-mockup-coral)]">*</span>
              </label>
              <Select
                value={form.subject?.id ? String(form.subject.id) : ""}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    subject: edoSubjectList?.find(
                      (s) => s.id === parseInt(v, 10)
                    ) ?? undefined,
                  } as any)
                }
                disabled={uploading || loadingSubject}
              >
                <SelectTrigger>
                  <SelectValue placeholder={translate("edocendoApp.edoVideostorePost.subject")} />
                </SelectTrigger>
                <SelectContent>
                  {edoSubjectList?.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  )) ?? null}
                </SelectContent>
              </Select>
              {loadingSubject && (
                <Loader2 className="mt-1 h-4 w-4 animate-spin text-[var(--color-mockup-text-light)]" />
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[var(--color-mockup-text)]">
                {translate("edocendoApp.edoVideostorePost.educationalStage")}{" "}
                <span className="text-[var(--color-mockup-coral)]">*</span>
              </label>
              <Select
                value={form.educationalStage || ""}
                onValueChange={(v) =>
                  setForm((prev) => ({
                    ...prev,
                    educationalStage: v as EdoEducationalStage,
                  }))
                }
                disabled={uploading}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[
                    EdoEducationalStage.INFANZIA,
                    EdoEducationalStage.PRIMARIA,
                    EdoEducationalStage.SECONDARIA_I,
                    EdoEducationalStage.SECONDARIA_II,
                  ].map((stage) => (
                    <SelectItem key={stage} value={stage}>
                      <Translate
                        contentKey={`edocendoApp.EdoEducationalStage.${stage}`}
                      />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={handleClose}>
            <Translate contentKey="entity.action.cancel" />
          </Button>
          <Button
            type="button"
            onClick={selectFiles}
            disabled={
              uploading ||
              updating ||
              !form.years?.length ||
              !form.subject ||
              !form.educationalStage
            }
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Aggiungi file"
            )}
          </Button>
        </DialogFooter>
        <input
          type="file"
          ref={fileInput}
          name="files"
          multiple
          hidden
          onChange={handleFiles}
        />
      </DialogContent>
    </Dialog>
  );
};

export default MultiUpload;
